import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  WhatsappLogo, Translate, SignIn, PhoneCall, EnvelopeSimple, MapPin, Buildings,
  TruckTrailer, Truck, Motorcycle, GasPump, Tire, LockKey, BatteryCharging,
  Clock, Crosshair, Certificate, ShieldCheck, Handshake, Wrench, Star,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { LOGO_URL } from "@/lib/api";
import { CONTENT, WA_URL } from "@/lib/translations";
import ContactForm from "@/components/ContactForm";

const HERO_BG = "https://images.unsplash.com/photo-1577525531917-c959191b5a90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxoaWdod2F5JTIwbmlnaHQlMjBtYWxheXNpYXxlbnwwfHx8fDE3ODYyNjM3NTd8MA&ixlib=rb-4.1.0&q=85";
const FLEET_IMGS = [
  "https://images.unsplash.com/photo-1601508836900-ee2aa7840a7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHx0b3clMjB0cnVja3xlbnwwfHx8fDE3ODYyMDI2MDR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1730514785075-b065c757b653?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHx0b3clMjB0cnVja3xlbnwwfHx8fDE3ODYyMDI2MDR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1686966933735-305bd8fe0a77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHx0b3clMjB0cnVja3xlbnwwfHx8fDE3ODYyMDI2MDR8MA&ixlib=rb-4.1.0&q=85",
];
const SERVICE_ICONS = [TruckTrailer, Truck, Motorcycle, GasPump, Tire, LockKey, BatteryCharging];
const OPS_ICONS = [Clock, Crosshair, MapPin, Certificate];
const TYRE_IMG = "https://images.unsplash.com/photo-1559674697-aea453b06ea9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxyb2Fkc2lkZSUyMGFzc2lzdGFuY2UlMjBtZWNoYW5pYyUyMHR5cmUlMjBjaGFuZ2V8ZW58MHx8fHwxNzg2MjYzNzQ2fDA&ixlib=rb-4.1.0&q=85";
const BATTERY_IMG = "https://images.unsplash.com/photo-1597766325363-f5576d851d6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxjYXIlMjBiYXR0ZXJ5JTIwc2VydmljZXxlbnwwfHx8fDE3ODYyNjM3NTd8MA&ixlib=rb-4.1.0&q=85";

function SectionHead({ overline, heading, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-primary">{overline}</p>
      <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">{heading}</h2>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem("pss_lang") || "en");
  useEffect(() => { localStorage.setItem("pss_lang", lang); }, [lang]);
  const t = CONTENT[lang];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Top strip: hotline + language toggle (top-right, directly above Login) */}
      <div className="border-b border-border/60 bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 lg:px-12">
          <p className="hidden items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground sm:flex">
            <PhoneCall size={13} className="text-primary" /> {t.topbar_hotline}
          </p>
          <div className="ml-auto flex items-center gap-1" data-testid="lang-toggle">
            <Translate size={14} className="mr-1 text-muted-foreground" />
            {["en", "bm"].map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`rounded-sm px-2.5 py-1 text-[11px] font-black uppercase tracking-widest transition-colors ${
                  lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="PSS Towing Auto Assist" className="h-11 w-11 rounded-sm object-contain" data-testid="landing-logo" />
            <div>
              <p className="font-display text-sm font-black uppercase tracking-widest text-foreground">PSS Towing</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">Auto Assist</p>
            </div>
          </div>
          <nav className="hidden items-center gap-7 lg:flex" data-testid="landing-nav">
            {Object.entries(t.nav).map(([key, label]) => (
              <a key={key} href={`#${key}`} data-testid={`nav-link-${key}`}
                className="text-xs font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:text-primary">
                {label}
              </a>
            ))}
          </nav>
          <Button
            data-testid="header-login-button"
            onClick={() => navigate("/login")}
            className="rounded-sm bg-primary px-6 font-bold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-amber-400 hover:text-black"
          >
            <SignIn size={16} className="mr-2" /> {t.login}
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_BG}')` }} />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-40">
          <p className="animate-fade-up font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-primary" data-testid="hero-overline">
            {t.hero.overline}
          </p>
          <h1 className="animate-fade-up stagger-1 mt-6 max-w-3xl font-display text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {t.hero.title.split("AUTO ASSIST")[0]}
            <span className="text-primary">AUTO ASSIST</span>
          </h1>
          <p className="animate-fade-up stagger-2 mt-6 max-w-xl text-base leading-relaxed text-slate-300" data-testid="hero-subtitle">
            {t.hero.subtitle}
          </p>
          <div className="animate-fade-up stagger-3 mt-10 flex flex-wrap items-center gap-4">
            <a href={WA_URL} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-cta"
              className="inline-flex items-center gap-2 rounded-sm bg-[#25D366] px-8 py-4 text-sm font-black uppercase tracking-wider text-black transition-transform hover:-translate-y-0.5 hover:brightness-110">
              <WhatsappLogo size={20} weight="fill" /> {t.hero.whatsapp}
            </a>
            <Button data-testid="hero-login-button" onClick={() => navigate("/login")} variant="outline"
              className="rounded-sm border-white/25 bg-black/40 px-8 py-6 text-sm font-bold uppercase tracking-wider text-white backdrop-blur-md transition-colors hover:bg-white/10 hover:text-white">
              {t.hero.portal}
            </Button>
          </div>
          <div className="animate-fade-up stagger-4 mt-12 flex flex-wrap gap-3">
            {t.hero.chips.map((c) => (
              <span key={c} className="rounded-sm border border-white/15 bg-black/40 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-slate-200 backdrop-blur-md">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="mx-auto max-w-7xl px-6 py-20 lg:px-12" data-testid="about-section">
        <SectionHead overline={t.about.overline} heading={t.about.heading} testid="about-head" />
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground" data-testid="about-intro">{t.about.intro}</p>
        <div className="mt-10 grid grid-cols-1 gap-px border border-border/60 bg-border/40 md:grid-cols-2">
          {[[t.about.vision_title, t.about.vision, Star], [t.about.mission_title, t.about.mission, ShieldCheck]].map(([title, body, Icon]) => (
            <div key={title} className="group bg-card p-8 transition-colors hover:bg-secondary/60">
              <Icon size={28} weight="duotone" className="text-primary transition-transform group-hover:-translate-y-1" />
              <h3 className="mt-4 font-display text-lg font-semibold tracking-tight">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
        <div className="mt-px flex flex-col gap-4 border border-border/60 bg-card p-8 md:flex-row md:items-center" data-testid="leadership-block">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-sm bg-primary font-display text-xl font-black text-primary-foreground">KA</div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.about.leadership_title}</p>
            <p className="mt-2 text-sm leading-relaxed text-foreground">{t.about.leadership}</p>
            <p className="mt-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">Encik Khairul Anuar · {t.about.leadership_role}</p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="border-y border-border/60 bg-card/40" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-12">
          <SectionHead overline={t.services.overline} heading={t.services.heading} testid="services-head" />
          <div className="mt-12 grid grid-cols-1 gap-px border border-border/60 bg-border/40 sm:grid-cols-2 lg:grid-cols-4">
            {t.services.items.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <div key={s.name} data-testid={`service-card-${i}`}
                  className={`group bg-card p-7 transition-colors hover:bg-secondary/60 ${i === 6 ? "sm:col-span-2 lg:col-span-1" : ""}`}>
                  <Icon size={30} weight="duotone" className="text-primary transition-transform group-hover:-translate-y-1" />
                  <h3 className="mt-4 font-display text-base font-semibold tracking-tight">{s.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fleet & Capabilities */}
      <section id="fleet" className="mx-auto max-w-7xl px-6 py-20 lg:px-12" data-testid="fleet-section">
        <SectionHead overline={t.fleet.overline} heading={t.fleet.heading} testid="fleet-head" />
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {t.fleet.vehicles.map((v, i) => (
            <div key={v.name} data-testid={`fleet-card-${i}`} className="group overflow-hidden rounded-sm border border-border bg-card">
              <div className="h-44 overflow-hidden">
                <img src={FLEET_IMGS[i]} alt={v.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <p className="font-display text-base font-bold tracking-tight">{v.name}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-widest text-primary">{v.role}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 grid grid-cols-1 gap-px border border-border/60 bg-border/40 sm:grid-cols-2 lg:grid-cols-4">
          {t.fleet.ops.map((o, i) => {
            const Icon = OPS_ICONS[i];
            return (
              <div key={o.title} className="bg-card p-6" data-testid={`ops-card-${i}`}>
                <Icon size={24} weight="duotone" className="text-primary" />
                <p className="mt-3 font-display text-sm font-bold tracking-tight">{o.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{o.desc}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-12 rounded-sm border border-border bg-card p-8" data-testid="compat-block">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">{t.fleet.compat_heading}</p>
          <p className="mt-2 text-sm text-muted-foreground">{t.fleet.compat_note}</p>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t.fleet.compat.map((g) => (
              <div key={g.group}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{g.group}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {g.brands.map((b) => (
                    <span key={b} className="rounded-sm border border-input bg-background px-3 py-1.5 font-mono text-xs font-bold text-foreground">{b}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners & Compliance */}
      <section id="partners" className="border-y border-border/60 bg-card/40" data-testid="partners-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-12">
          <SectionHead overline={t.partners.overline} heading={t.partners.heading} testid="partners-head" />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            {t.partners.list.map((p, i) => (
              <div key={p.name} data-testid={`partner-card-${i}`}
                className="flex items-center gap-5 rounded-sm border border-border bg-card p-7 transition-colors hover:border-primary/40">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-sm bg-primary/10">
                  {i === 0 ? <Wrench size={28} className="text-primary" /> : <Handshake size={28} className="text-primary" />}
                </div>
                <div>
                  <p className="font-display text-lg font-bold tracking-tight">{p.name}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-sm border border-primary/30 bg-primary/5 p-7" data-testid="compliance-block">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">{t.partners.compliance_heading}</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {t.partners.compliance.map((c) => (
                <p key={c} className="flex items-center gap-2 text-sm font-semibold">
                  <ShieldCheck size={18} className="shrink-0 text-primary" /> {c}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services strip images */}
      <section className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-6 py-12 lg:px-12">
        {[TYRE_IMG, BATTERY_IMG].map((src) => (
          <div key={src} className="h-52 overflow-hidden rounded-sm border border-border">
            <img src={src} alt="PSS service" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
          </div>
        ))}
      </section>

      {/* Contact */}
      <section id="contact" className="mx-auto max-w-7xl px-6 pb-20 lg:px-12" data-testid="contact-section">
        <SectionHead overline={t.contact.overline} heading={t.contact.heading} testid="contact-head" />
        <div className="mt-12 grid grid-cols-1 gap-px border border-border/60 bg-border/40 sm:grid-cols-2 lg:grid-cols-3">
          <div className="bg-card p-7" data-testid="contact-hq">
            <Buildings size={24} className="text-primary" />
            <p className="mt-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.contact.hq_title}</p>
            <p className="mt-2 text-sm leading-relaxed font-semibold">{t.contact.hq}</p>
          </div>
          <div className="bg-card p-7" data-testid="contact-kl">
            <MapPin size={24} className="text-primary" />
            <p className="mt-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.contact.kl_title}</p>
            <p className="mt-2 text-sm leading-relaxed font-semibold">{t.contact.kl}</p>
          </div>
          <div className="bg-card p-7" data-testid="contact-phone">
            <PhoneCall size={24} className="text-primary" />
            <p className="mt-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.contact.phone_title}</p>
            <a href={`tel:${t.contact.phone.replace(/\s|-/g, "")}`} className="mt-2 block text-sm font-bold text-foreground transition-colors hover:text-primary">{t.contact.phone}</a>
            <a href={`mailto:${t.contact.email}`} className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary">
              <EnvelopeSimple size={14} /> {t.contact.email}
            </a>
          </div>
        </div>
        <ContactForm t={t.contact} />
      </section>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-card" data-testid="landing-footer">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-10 lg:flex-row lg:items-center lg:justify-between lg:px-12">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="PSS Towing" className="h-9 w-9 rounded-sm object-contain" />
            <div>
              <p className="font-display text-sm font-black uppercase tracking-widest">PSS Towing Auto Assist</p>
              <p className="text-xs text-muted-foreground">{t.footer.tagline}</p>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            <p className="font-mono">{t.footer.reg}</p>
            <p>© 2025 PSS Towing Auto Assist. {t.footer.rights}</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Chat Now */}
      <a href={WA_URL} target="_blank" rel="noreferrer" data-testid="whatsapp-fab" aria-label={t.fab_label}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-4 text-sm font-black uppercase tracking-wider text-black shadow-lg shadow-emerald-500/30 transition-transform hover:-translate-y-1 hover:brightness-110">
        <WhatsappLogo size={24} weight="fill" />
        <span className="hidden sm:inline">{t.fab_label}</span>
      </a>
    </div>
  );
}
